/**
 * 水印工具函数
 * 使用 Canvas 生成水印图片，并应用到页面
 */

export interface WatermarkOptions {
  text?: string;
  font?: string;
  fillStyle?: string;
  width?: number;
  height?: number;
  rotate?: number;
  zIndex?: number;
}

/**
 * 生成水印
 */
export function createWatermark(options: WatermarkOptions = {}): (() => void) | void {
  const {
    text = '',
    font = '16px Arial',
    fillStyle = 'rgba(0, 0, 0, 0.15)',
    width = 200,
    height = 150,
    rotate = -20,
    zIndex = 9999
  } = options;

  if (!text) {
    console.warn('水印文本为空');
    return;
  }

  // 创建canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // 设置文字样式
  ctx.font = font;
  ctx.fillStyle = fillStyle;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // 旋转画布
  ctx.translate(width / 2, height / 2);
  ctx.rotate((rotate * Math.PI) / 180);
  ctx.translate(-width / 2, -height / 2);

  // 绘制文字
  ctx.fillText(text, width / 2, height / 2);

  // 将canvas转为base64图片
  const base64Url = canvas.toDataURL();

  // 创建水印容器
  const watermarkDiv = document.createElement('div');
  watermarkDiv.id = 'watermark-container';
  watermarkDiv.style.position = 'fixed';
  watermarkDiv.style.top = '0';
  watermarkDiv.style.left = '0';
  watermarkDiv.style.width = '100%';
  watermarkDiv.style.height = '100%';
  watermarkDiv.style.pointerEvents = 'none';
  watermarkDiv.style.zIndex = String(zIndex);
  watermarkDiv.style.backgroundImage = `url(${base64Url})`;
  watermarkDiv.style.backgroundRepeat = 'repeat';
  watermarkDiv.style.backgroundPosition = '0 0';

  // 移除旧的水印（如果存在）
  const oldWatermark = document.getElementById('watermark-container');
  if (oldWatermark) {
    oldWatermark.remove();
  }

  // 添加到body
  document.body.appendChild(watermarkDiv);

  // 使用MutationObserver监控水印是否被删除
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        if (node === watermarkDiv || (node.nodeType === 1 && (node as Element).contains(watermarkDiv))) {
          // 水印被删除，重新创建
          document.body.appendChild(watermarkDiv);
        }
      });
    });
  });

  // 监控body的变化
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // 监控样式变化
  const styleObserver = new MutationObserver(() => {
    const currentWatermark = document.getElementById('watermark-container');
    if (currentWatermark) {
      const display = window.getComputedStyle(currentWatermark).display;
      const opacity = window.getComputedStyle(currentWatermark).opacity;
      if (display === 'none' || opacity === '0') {
        currentWatermark.style.display = 'block';
        currentWatermark.style.opacity = '1';
      }
    }
  });

  if (watermarkDiv) {
    styleObserver.observe(watermarkDiv, {
      attributes: true,
      attributeFilter: ['style', 'class']
    });
  }

  // 返回清理函数
  return () => {
    observer.disconnect();
    styleObserver.disconnect();
    const watermark = document.getElementById('watermark-container');
    if (watermark) {
      watermark.remove();
    }
  };
}

/**
 * 移除水印
 */
export function removeWatermark(): void {
  const watermark = document.getElementById('watermark-container');
  if (watermark) {
    watermark.remove();
  }
}

export default {
  createWatermark,
  removeWatermark
};
